import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { Edit2, Download, Search, ShoppingBag, Users } from 'react-feather';

import config from '../../../../config';
import '../../../ControlPanel/sadmin.css';
import '../../Settings/settings.css';
import KpiStrip from '../../Settings/shared/KpiStrip';
import Pagination from '../../Settings/shared/Pagination';
import useEntityCrud from '../../Settings/shared/useEntityCrud';

/**
 * /admin/tenant/viewtenant — Tenants list, redesigned to the v3 admin
 * surface.
 *
 * Reuses the same primitives as the General Settings hub: sadmin hero,
 * KPI strip, glass list card with search and pagination, status pills,
 * action icons. The Add / Edit flows keep their existing dedicated routes
 * (the underlying forms are too rich — leases, status changes, dates,
 * data-capture method — to fold into a single modal at this point).
 */
const TenantView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchInputRef = useRef(null);

    const manageTenant = localStorage.getItem('manageTenants');
    const manageGeneralSettings = localStorage.getItem('manageGeneralSettings');
    const accessTenant = localStorage.getItem('accessTenants');
    const accessGeneralSettings = localStorage.getItem('accessGeneralSettings');
    const canManage = manageTenant === '1' || manageGeneralSettings === '1';
    const canAccess = canManage || accessTenant === '1' || accessGeneralSettings === '1';

    const loginName = localStorage.getItem('loginName');
    const loginId = localStorage.getItem('loginId');
    const concessionaireId = localStorage.getItem('concessionaireId');
    const propertyAccess = localStorage.getItem('propertyAccess');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    // URL-backed filter state so deep links survive refresh.
    const q = searchParams.get('q') || '';
    const page = Number(searchParams.get('page')) || 1;

    const [qInput, setQInput] = useState(q);
    useEffect(() => { setQInput(q); }, [q]);
    useEffect(() => {
        const t = setTimeout(() => {
            if (qInput !== q) {
                const next = new URLSearchParams(searchParams);
                if (qInput) next.set('q', qInput); else next.delete('q');
                next.delete('page');
                setSearchParams(next, { replace: true });
            }
        }, 250);
        return () => clearTimeout(t);
    }, [qInput, q, searchParams, setSearchParams]);

    const { rows, paged, total, pageCount, page: safePage, loading } = useEntityCrud({
        endpoint: `${config.hostname}/tenant/getenantsbyconcessionaire`,
        payload: { tenant_concessionaire_id: concessionaireId, property_id: propertyAccess },
        searchFields: [
            'tenant_store_name',
            'tenant_group_name',
            'tenant_code',
            'tenant_unit_code',
            'tenant_property_id.property_name',
            'tenant_category_id.category_name',
        ],
        q,
        page,
        pageSize: 15,
    });

    // KPI counters — active / disabled / live.
    const disabled = rows.filter((r) => r.tenant_disable === true).length;
    const live = rows.filter((r) => r.tenant_disable !== true && r.tenant_status === 'Live').length;
    const tiles = useMemo(() => ([
        { label: t('Total tenants'), value: rows.length, icon: <ShoppingBag size={16} />, tone: 'accent' },
        { label: t('Live'), value: live, icon: <Users size={16} />, tone: 'green' },
        { label: t('Disabled'), value: disabled, tone: 'amber' },
    ]), [rows.length, live, disabled, t]);

    // ── Auth & shortcuts ────────────────────────────────────────────
    useEffect(() => {
        if (!canAccess) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canAccess]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    // ── Row + toolbar actions ───────────────────────────────────────
    const handleAdd = useCallback(() => {
        navigate(`${process.env.PUBLIC_URL}/admin/tenant/newtenant/default`);
    }, [navigate]);

    const handleEdit = useCallback((id) => {
        navigate(`${process.env.PUBLIC_URL}/admin/tenant/tenantedit/${id}`);
    }, [navigate]);

    const handleView = useCallback((id) => {
        navigate(`${process.env.PUBLIC_URL}/admin/tenant/tenantdetail/${id}`);
    }, [navigate]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    const handleExport = useCallback(() => {
        const exportData = rows.map((t) => ({
            'Store Name': t.tenant_store_name,
            'Group Name': t.tenant_group_name,
            'Unit Code': t.tenant_unit_code,
            'Tenant Code': t.tenant_code,
            'Property': t.tenant_property_id?.property_name,
            'Category': t.tenant_category_id?.category_name,
            'Subcategory': t.tenant_subcategory_id?.subcategory_name,
            'Status': t.tenant_disable ? 'Disable' : (t.tenant_status || '-'),
            'Live Date': formatDate(t.tenant_live_date),
            'Data Capture Method': t.tenant_data_capture_method,
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Tenants');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, 'Tenants.xlsx');

        // Audit-log the export (same payload shape the V2 page used).
        axios.post(
            `${config.hostname}/log`,
            {
                created_by: loginId,
                concessionaire_id: concessionaireId,
                module: 'Tenant List',
                task: `${loginName} exported tenant list to excel`,
            },
            { headers },
        ).catch((err) => console.error('Log insert failed', err));
    }, [rows, headers, concessionaireId, loginId, loginName]);

    if (!canAccess) return null;

    // ── Render ──────────────────────────────────────────────────────
    return (
        <Fragment>
            <div className="v3-sadmin">
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0 }}>{t('General settings')}</p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">{t('Tenants')}</span>
                        </h1>
                        <p className="v3-body v3-sadmin-sub">
                            {t('All tenants across the properties you have access to. Search by store, group, code or property.')}
                        </p>
                    </div>
                    <div className="v3-sadmin-hero-actions">
                        <button
                            type="button"
                            className="v3-btn v3-btn-ghost"
                            onClick={handleExport}
                            disabled={!rows.length}
                            title={t('Export tenants to Excel')}
                        >
                            <Download size={14} /> {t('Export')}
                        </button>
                        {canManage && (
                            <button type="button" className="v3-btn v3-btn-primary" onClick={handleAdd}>
                                {t('+ Add tenant')}
                            </button>
                        )}
                    </div>
                </div>

                <KpiStrip tiles={tiles} />

                <div className="v3-card v3-sadmin-list-card">
                    <div className="v3-sadmin-list-header">
                        <div className="v3-sadmin-list-title">
                            <h2 className="v3-title">{t('Tenants')}</h2>
                            <span className="v3-sadmin-list-count">{total}</span>
                        </div>
                        <div className="v3-sadmin-list-actions">
                            <label className="v3-sadmin-search">
                                <Search size={14} />
                                <input
                                    ref={searchInputRef}
                                    type="search"
                                    value={qInput}
                                    onChange={(e) => setQInput(e.target.value)}
                                    placeholder={t('Search store, group, code, property…  (/)')}
                                    aria-label={t('Search tenants')}
                                />
                            </label>
                        </div>
                    </div>
                    <div className="v3-sadmin-table-wrap">
                        {loading ? (
                            <div className="v3-sadmin-empty"><p>{t('Loading tenants…')}</p></div>
                        ) : total === 0 ? (
                            <div className="v3-sadmin-empty">
                                <Search size={28} />
                                <h3>{t('No tenants found')}</h3>
                                <p>{q ? t('Try clearing the search.') : t('Add your first tenant to get started.')}</p>
                            </div>
                        ) : (
                            <table className="v3-sadmin-table">
                                <thead>
                                    <tr>
                                        <th>{t('Store')}</th>
                                        <th>{t('Group')}</th>
                                        <th>{t('Unit')}</th>
                                        <th>{t('Tenant code')}</th>
                                        <th>{t('Property')}</th>
                                        <th>{t('Category')}</th>
                                        <th>{t('Status')}</th>
                                        {canManage && <th style={{ width: 80 }}>{t('Actions')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paged.map((row) => {
                                        const statusLabel = row.tenant_disable
                                            ? t('Disabled')
                                            : (row.tenant_status || '—');
                                        const pillClass = row.tenant_disable
                                            ? 'off'
                                            : row.tenant_status === 'Live'
                                                ? 'live'
                                                : row.tenant_status === 'Closed'
                                                    ? 'danger'
                                                    : 'warn';
                                        return (
                                            <tr key={row._id} onClick={() => handleView(row._id)}>
                                                <td className="v3-sadmin-cell-strong">{row.tenant_store_name}</td>
                                                <td className="v3-sadmin-cell-muted">{row.tenant_group_name || '—'}</td>
                                                <td><span className="v3-sadmin-cell-code">{row.tenant_unit_code}</span></td>
                                                <td><span className="v3-sadmin-cell-code">{row.tenant_code}</span></td>
                                                <td className="v3-sadmin-cell-muted">{row.tenant_property_id?.property_name || '—'}</td>
                                                <td className="v3-sadmin-cell-muted">{row.tenant_category_id?.category_name || '—'}</td>
                                                <td>
                                                    <span className={`v3-sadmin-pill ${pillClass}`}>
                                                        {statusLabel}
                                                    </span>
                                                </td>
                                                {canManage && (
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <div className="v3-sadmin-row-actions">
                                                            <button
                                                                type="button"
                                                                className="v3-sadmin-icon-btn"
                                                                onClick={() => handleEdit(row._id)}
                                                                aria-label={t('Edit')}
                                                            >
                                                                <Edit2 size={13} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <Pagination
                        page={safePage}
                        pageCount={pageCount}
                        total={total}
                        pageSize={15}
                        onPageChange={(p) => {
                            const next = new URLSearchParams(searchParams);
                            if (p > 1) next.set('page', p); else next.delete('page');
                            setSearchParams(next, { replace: true });
                        }}
                    />
                </div>
            </div>
        </Fragment>
    );
};

export default TenantView;
